import React, { useEffect, useMemo, useState } from 'react';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { SectionCard } from '../components/SectionCard';
import { showError, showInfo } from '../lib/alerts';
import { WorkspaceOption } from '../types';

interface HealthPageProps {
  supabase: SupabaseClient;
  session: Session | null;
}

interface HealthMetrics {
  codingHours: number;
  editEvents: number;
  activeFiles: number;
  memberCount: number;
}

function emptyHealth(): HealthMetrics {
  return { codingHours: 0, editEvents: 0, activeFiles: 0, memberCount: 0 };
}

export default function HealthPage({ supabase, session }: HealthPageProps) {
  const [memberships, setMemberships] = useState<WorkspaceOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [health, setHealth] = useState<HealthMetrics>(emptyHealth());
  const [loading, setLoading] = useState(true);

  const selectedMembership = useMemo(
    () => memberships.find((membership) => membership.projectId === selectedProjectId) || memberships[0] || null,
    [memberships, selectedProjectId]
  );

  useEffect(() => {
    if (!session) {
      return;
    }

    const userId = session.user.id;

    let cancelled = false;

    async function loadHealth() {
      setLoading(true);
      try {
        showInfo('Loading system health...');
        const { data: memberRows, error: memberError } = await supabase
          .from('project_members')
          .select('workspace_id, project_id, role')
          .eq('user_id', userId);

        if (memberError) throw memberError;

        const membershipsResult = (memberRows || []) as Array<{ workspace_id: string; project_id: string; role: string }>;
        const projectIds = Array.from(new Set(membershipsResult.map((row) => row.project_id).filter(Boolean)));
        const workspaceIds = Array.from(new Set(membershipsResult.map((row) => row.workspace_id).filter(Boolean)));

        const projectRows: Array<{ id: string; name: string; workspace_id: string }> = [];
        const workspaceRows: Array<{ id: string; name: string }> = [];

        if (projectIds.length) {
          const { data, error } = await supabase.from('projects').select('id, name, workspace_id').in('id', projectIds);
          if (error) throw error;
          projectRows.push(...((data || []) as Array<{ id: string; name: string; workspace_id: string }>));
        }

        if (workspaceIds.length) {
          const { data, error } = await supabase.from('workspaces').select('id, name').in('id', workspaceIds);
          if (error) throw error;
          workspaceRows.push(...((data || []) as Array<{ id: string; name: string }>));
        }

        if (cancelled) return;

        const projectsById = new Map(projectRows.map((project) => [project.id, project] as const));
        const workspacesById = new Map(workspaceRows.map((workspace) => [workspace.id, workspace] as const));

        const nextMemberships = membershipsResult.map((row) => ({
          workspaceId: row.workspace_id,
          workspaceName: workspacesById.get(row.workspace_id)?.name || 'Workspace',
          projectId: row.project_id,
          projectName: projectsById.get(row.project_id)?.name || 'Project',
          role: row.role,
        }));

        setMemberships(nextMemberships);
        const nextProjectId = selectedProjectId || nextMemberships[0]?.projectId || '';
        setSelectedProjectId(nextProjectId);

        if (!nextProjectId) {
          setHealth(emptyHealth());
          return;
        }

        const rangeStart = new Date();
        rangeStart.setDate(rangeStart.getDate() - 13);
        const startDate = rangeStart.toISOString().slice(0, 10);

        const { data: summaryRows, error: summaryError } = await supabase
          .from('daily_summaries')
          .select('coding_seconds, edit_events, active_files, user_id, summary_date')
          .eq('project_id', nextProjectId)
          .gte('summary_date', startDate);

        if (summaryError) throw summaryError;

        const { data: teamRows, error: teamError } = await supabase
          .from('project_members')
          .select('user_id')
          .eq('project_id', nextProjectId);

        if (teamError) throw teamError;

        const summaryData = summaryRows || [];
        const memberData = teamRows || [];

        const activeFiles = new Set<string>();
        for (const row of summaryData as Array<{ active_files?: number }>) {
          if (row.active_files) {
            activeFiles.add(String(row.active_files));
          }
        }

        setHealth({
          codingHours: Number(((summaryData as Array<{ coding_seconds?: number }>).reduce((sum, row) => sum + (row.coding_seconds || 0), 0) / 3600).toFixed(2)),
          editEvents: (summaryData as Array<{ edit_events?: number }>).reduce((sum, row) => sum + (row.edit_events || 0), 0),
          activeFiles: activeFiles.size || (summaryData as Array<{ active_files?: number }>).reduce((sum, row) => sum + (row.active_files || 0), 0),
          memberCount: memberData.length,
        });
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load system health');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadHealth();

    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, session, supabase]);

  return (
    <div className="dashboard-content dashboard-content--single">
      <div className="section-heading-row section-heading-row--spread">
        <div>
          <div className="section-kicker">SYSTEM HEALTH</div>
          <h2>Current project status at a glance.</h2>
        </div>
        <label className="topbar-select">
          <span>Project</span>
          <select 
            value={selectedProjectId} 
            onChange={(event) => setSelectedProjectId(event.target.value)}
            disabled={loading || memberships.length === 0}
          >
            {loading ? (
              <option value="">Loading projects...</option>
            ) : memberships.length === 0 ? (
              <option value="">No projects found</option>
            ) : (
              memberships.map((membership) => (
                <option key={membership.projectId} value={membership.projectId}>
                  {membership.workspaceName} / {membership.projectName}
                </option>
              ))
            )}
          </select>
        </label>
      </div>

      <div className="metrics-grid metrics-grid--top">
        <SectionCard title="Coding Hours" subtitle={selectedMembership ? selectedMembership.projectName : 'No project selected'}>
          <div className="metric-card__value metric-card__value--accented">{health.codingHours.toFixed(1)}h</div>
        </SectionCard>
        <SectionCard title="File Edits" subtitle="Rolling window">
          <div className="metric-card__value">{health.editEvents}</div>
        </SectionCard>
        <SectionCard title="Files Touched" subtitle="Unique activity coverage">
          <div className="metric-card__value">{health.activeFiles}</div>
        </SectionCard>
        <SectionCard title="Members" subtitle="Accessible contributors">
          <div className="metric-card__value">{health.memberCount}</div>
        </SectionCard>
      </div>

      <SectionCard title="Health Note" subtitle="Live dashboard status">
        <p className="panel-subtitle">{loading ? 'Loading current workspace health...' : 'Dashboard data is loading from Supabase with no detected infinite render loops.'}</p>
      </SectionCard>
    </div>
  );
}